// src/components/admin/CuponNuevo.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';

/* ===== BASE robusta: localhost o dominio ===== */
const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE = resolveApiBase(); // <- se usará para /api/admin/cupones y /api/productosweb

function useMiniToast() {
  const [msg, setMsg] = useState('');
  const [kind, setKind] = useState('ok'); // 'ok' | 'err'
  const show = (text, k='ok') => {
    setKind(k);
    // Mostrar mensaje sin temporizador automático (sin delays cosméticos)
    setMsg(text);
  };
  const Toast = () =>
    msg ? (
      <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-3 py-2 rounded-lg text-white shadow
        ${kind === 'ok' ? 'bg-emerald-600' : 'bg-rose-600'}`}>
        {msg}
      </div>
    ) : null;
  return { show, Toast };
}

export default function CuponNuevo() {
  const { show, Toast } = useMiniToast();

  // Form fields
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [porcentaje, setPorcentaje] = useState(10);
  const [color, setColor] = useState('#0ea5e9');
  const [inicio, setInicio] = useState(''); // datetime-local string
  const [fin, setFin] = useState('');

  // Producto (buscador local)
  const [qProd, setQProd] = useState('');
  const [allProducts, setAllProducts] = useState([]); // [{id,nombre,marca,presentacion}, ...]
  const [prodLoading, setProdLoading] = useState(false);
  const [prodError, setProdError] = useState('');
  const [producto, setProducto] = useState(null); // {id,nombre,marca,presentacion}

  // Estado de submit
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const abortRef = useRef(null);

  // Cargar catálogo para búsqueda (como en tu Catálogo)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setProdLoading(true);
        setProdError('');
        const r = await fetch(`${API_BASE}/api/productosweb`, { credentials: 'include' });
        const data = await r.json();
        if (!alive) return;
        const list = (Array.isArray(data) ? data : []).map((p) => ({
          id: p.id,
          nombre: p.nombre,
          marca: p.marca,
          presentacion: p.presentacion,
        }));
        setAllProducts(list);
      } catch (e) {
        if (!alive) return;
        setProdError('No se pudo cargar el catálogo para búsqueda.');
      } finally {
        if (alive) setProdLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Filtrado local del producto
  const prodOpts = useMemo(() => {
    const q = qProd.trim().toLowerCase();
    if (!q) return [];
    const hits = allProducts.filter((p) =>
      (p.nombre || '').toLowerCase().includes(q) ||
      (p.marca || '').toLowerCase().includes(q) ||
      (p.presentacion || '').toLowerCase().includes(q)
    );
    return hits.slice(0, 50);
  }, [qProd, allProducts]);

  // Validación simple en cliente
  const validateClient = () => {
    const pct = Number(porcentaje);
    if (isNaN(pct) || pct < 0 || pct > 100) return 'El porcentaje debe estar entre 0 y 100.';
    if (!inicio || !fin) return 'Debes completar fechas de inicio y fin.';
    const d1 = new Date(inicio);
    const d2 = new Date(fin);
    if (isNaN(d1) || isNaN(d2)) return 'Fechas inválidas.';
    if (d1 >= d2) return 'La fecha de inicio debe ser anterior a la de fin.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');
    const v = validateClient();
    if (v) { setErrMsg(v); show(v, 'err'); return; }

    setLoading(true);

    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;
    const t = setTimeout(() => controller.abort(), 15000);

    try {
      const payload = {
        nombre,
        descripcion,
        porcentaje_descuento: Number(porcentaje),
        color_hex: color,
        fecha_inicio: inicio, // el backend lo convierte a DATETIME
        fecha_fin: fin,
        producto_id: producto?.id || null,
      };

      // RUTA CORRECTA según tu server:
      // app.use('/api', cuponesRouter)
      // router.post('/admin/cupones', ...)
      // => POST /api/admin/cupones
      const r = await fetch(`${API_BASE}/api/admin/cupones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify(payload)
      });

      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        const msg = j?.msg || j?.error || r.statusText || 'Error al crear';
        throw new Error(msg);
      }

      show('✅ Cupón creado');
      // reset
      setNombre(''); setDescripcion(''); setPorcentaje(10); setColor('#0ea5e9');
      setInicio(''); setFin('');
      setProducto(null); setQProd('');
    } catch (err) {
      if (err.name === 'AbortError') {
        setErrMsg('Tiempo de espera agotado. Intenta nuevamente.');
        show('Tiempo de espera agotado', 'err');
      } else {
        const msg = String(err?.message || err);
        setErrMsg(msg);
        show(msg, 'err');
      }
    } finally {
      clearTimeout(t);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      <Toast />
      <div className="flex items-start justify-between">
        <h1 className="text-2xl font-bold">Crear cupón</h1>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700"
          disabled={loading}
        >
          ← Volver
        </button>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 grid gap-5 bg-white rounded-2xl p-5 shadow">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium">Nombre del cupón</label>
          <input
            value={nombre}
            onChange={(e)=>setNombre(e.target.value)}
            required
            className="w-full rounded-lg border px-3 py-2"
            placeholder="Back To School"
            disabled={loading}
          />
        </div>

        {/* Producto (búsqueda local) */}
        <div>
          <label className="block text-sm font-medium">Producto (búsqueda como en Catálogo)</label>
          <input
            value={qProd}
            onChange={(e)=>{ setQProd(e.target.value); if (!e.target.value) setProducto(null); }}
            className="w-full rounded-lg border px-3 py-2"
            placeholder={prodLoading ? 'Cargando catálogo…' : 'Buscar por nombre, marca o presentación...'}
            disabled={prodLoading || loading}
          />

          {prodLoading && <div className="text-xs mt-1 text-slate-500">Cargando catálogo…</div>}
          {!!prodError && <div className="text-xs mt-1 text-rose-600">{prodError}</div>}

          {prodOpts.length > 0 && (
            <div className="mt-2 max-h-48 overflow-auto rounded-lg border bg-white shadow">
              {prodOpts.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => { setProducto(p); setQProd(p.nombre); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50"
                  title={[p.marca, p.presentacion].filter(Boolean).join(' · ')}
                >
                  <div className="font-medium">{p.nombre}</div>
                  <div className="text-xs text-slate-500">
                    {[p.marca, p.presentacion].filter(Boolean).join(' · ') || '—'}
                  </div>
                </button>
              ))}
              {prodOpts.length === 50 && (
                <div className="px-3 py-2 text-xs text-slate-500 border-t">
                  Mostrando 50 primeros resultados. Afina tu búsqueda.
                </div>
              )}
            </div>
          )}

          {producto && (
            <p className="text-sm mt-1">
              Seleccionado:{' '}
              <span className="font-medium">{producto.nombre}</span>
              {(producto.marca || producto.presentacion) && (
                <span className="text-slate-500">
                  {' '}— {[producto.marca, producto.presentacion].filter(Boolean).join(' · ')}
                </span>
              )}
            </p>
          )}
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-medium">Descripción</label>
          <textarea
            value={descripcion}
            onChange={(e)=>setDescripcion(e.target.value)}
            rows={3}
            className="w-full rounded-lg border px-3 py-2"
            placeholder="Descuento para jeringas 10ml"
            disabled={loading}
          />
        </div>

        {/* % y fechas */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium">% Descuento</label>
            <input
              type="number" min={0} max={100} step="0.01"
              value={porcentaje}
              onChange={(e)=>setPorcentaje(e.target.value)}
              required
              className="w-full rounded-lg border px-3 py-2"
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Fecha inicio</label>
            <input
              type="datetime-local"
              value={inicio}
              onChange={(e)=>setInicio(e.target.value)}
              required
              className="w-full rounded-lg border px-3 py-2"
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Fecha fin</label>
            <input
              type="datetime-local"
              value={fin}
              onChange={(e)=>setFin(e.target.value)}
              required
              className="w-full rounded-lg border px-3 py-2"
              disabled={loading}
            />
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="block text-sm font-medium">Color</label>
          <input
            type="color"
            value={color}
            onChange={(e)=>setColor(e.target.value)}
            className="h-10 w-20 cursor-pointer"
            disabled={loading}
          />
        </div>

        {/* Submit */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {loading ? 'Guardando…' : 'Crear cupón'}
          </button>
          {!!errMsg && <span className="text-sm text-rose-600">{errMsg}</span>}
        </div>
      </form>
    </div>
  );
}
